import brandOverrides from "../../brand.config.json"
import { cloneConfig, DEFAULT_CONFIG } from "./defaults"
import { readEnvPatch, runtimeConfigUrl } from "./env"
import { applyConfigPatch, finalizeConfig, validateConfigPatch } from "./schema"
import type { AppConfig, LoadedConfig } from "./types"

export interface LoadConfigOptions {
  /** `null` skips the runtime layer (used by tests). */
  runtimeUrl?: string | null
  runtimeTimeoutMs?: number
  fetchImpl?: typeof fetch
}

async function fetchJson(url: string, timeoutMs: number, fetchImpl: typeof fetch): Promise<unknown> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetchImpl(url, {
      signal: controller.signal,
      cache: "no-store",
      headers: { accept: "application/json" },
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return await response.json()
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Resolves the effective configuration from four layers, later layers win:
 *
 *   1. `src/config/defaults.ts`
 *   2. `brand.config.json` (bundled)
 *   3. `VITE_APP_*` environment variables (bundled)
 *   4. `/app-config.json` fetched at runtime — no rebuild required
 */
export async function loadAppConfig(options: LoadConfigOptions = {}): Promise<LoadedConfig> {
  const warnings: string[] = []
  const sources: string[] = ["defaults"]
  let config = cloneConfig(DEFAULT_CONFIG)

  const brand = validateConfigPatch(brandOverrides, "brand.config.json")
  warnings.push(...brand.warnings)
  if (Object.keys(brand.patch).length > 0) {
    config = applyConfigPatch(config, brand.patch)
    sources.push("brand.config.json")
  }

  const env = validateConfigPatch(readEnvPatch(), "environment")
  warnings.push(...env.warnings)
  if (Object.keys(env.patch).length > 0) {
    config = applyConfigPatch(config, env.patch)
    sources.push("environment")
  }

  const url = options.runtimeUrl === undefined ? runtimeConfigUrl() : options.runtimeUrl
  if (url && typeof window !== "undefined") {
    try {
      const raw = await fetchJson(
        url,
        options.runtimeTimeoutMs ?? 4000,
        options.fetchImpl ?? fetch.bind(globalThis),
      )
      const runtime = validateConfigPatch(raw, url)
      warnings.push(...runtime.warnings)
      if (Object.keys(runtime.patch).length > 0) {
        config = applyConfigPatch(config, runtime.patch)
        sources.push(url)
      }
    } catch (error) {
      // A missing runtime file is expected: the bundled configuration is used.
      warnings.push(`runtime configuration not applied (${url}): ${String(error)}`)
    }
  }

  return { config: finalizeConfig(config), warnings, sources }
}

/** Pure helper used by tests and by `loadAppConfig`. */
export function resolveConfig(layers: Array<{ source: string; value: unknown }>): {
  config: AppConfig
  warnings: string[]
} {
  let config = cloneConfig(DEFAULT_CONFIG)
  const warnings: string[] = []
  for (const layer of layers) {
    const result = validateConfigPatch(layer.value, layer.source)
    warnings.push(...result.warnings)
    config = applyConfigPatch(config, result.patch)
  }
  return { config: finalizeConfig(config), warnings }
}
